<template>
  <b-card>
    <section
      v-if="$can('read', 'A.User')"
      class="admin-users-statistics mb-1"
      aria-labelledby="user-statistics-title"
      :aria-busy="loadingUsers"
    >
      <h2
        id="user-statistics-title"
        class="h5 mb-50"
      >
        {{ $t('adminUsers.statistics.title') }}
      </h2>
      <p class="text-muted small mb-50">
        {{ $t('adminUsers.statistics.description') }}
      </p>
      <div class="admin-users-statistics-grid">
        <button
          v-for="stat in userStatistics"
          :key="stat.key"
          type="button"
          class="admin-users-statistic text-left rounded"
          :class="{ 'border-primary': selectedStatus === stat.key }"
          :data-status="stat.key"
          :aria-pressed="selectedStatus === stat.key"
          :disabled="loadingUsers || usersLoadError"
          @click="selectedStatus = stat.key"
        >
          <span class="d-flex justify-content-between align-items-center mb-25">
            <span
              class="h4 font-weight-bolder mb-0"
              :class="`text-${stat.variant}`"
              data-statistic-value
            >{{ loadingUsers || usersLoadError ? '—' : formatCount(stat.count) }}</span>
            <feather-icon
              :icon="stat.icon"
              :class="`text-${stat.variant}`"
              size="16"
              aria-hidden="true"
            />
          </span>
          <span class="d-block small font-weight-bold">{{ $t(`adminUsers.statistics.${stat.label}`) }}</span>
        </button>
      </div>
    </section>
    <b-alert
      :show="usersLoadError"
      variant="danger"
    >
      <div class="alert-body d-flex flex-wrap align-items-center justify-content-between">
        <span>{{ $t('adminUsers.statistics.loadError') }}</span>
        <b-button
          size="sm"
          variant="outline-danger"
          @click="getPayments"
        >
          {{ $t('adminUsers.statistics.retry') }}
        </b-button>
      </div>
    </b-alert>
    <div class="admin-users-filters mb-50">
      <b-form-input
        id="filter-input"
        v-model="nameFilter"
        type="search"
        size="sm"
        :placeholder="$t('adminUsers.filters.search')"
        :aria-label="$t('adminUsers.filters.search')"
        :disabled="loadingUsers || usersLoadError"
      />
      <b-form-group
        class="mb-0"
        label-cols="auto"
        label-size="sm"
        label-for="approval-filter"
        :label="$t('adminUsers.filters.approval')"
      >
        <b-form-select
          id="approval-filter"
          v-model="approvalFilter"
          size="sm"
          :options="approvalOptions"
          :disabled="loadingUsers || usersLoadError"
        />
      </b-form-group>
      <b-button
        size="sm"
        variant="flat-primary"
        :disabled="!hasFilters || loadingUsers || usersLoadError"
        @click="clearFilters"
      >
        {{ $t('adminUsers.filters.clear') }}
      </b-button>
    </div>
    <div class="d-flex flex-wrap align-items-center mb-1">
      <b-badge
        v-if="selectedStatus !== 'ALL'"
        variant="light-primary"
        class="mr-50"
      >
        {{ $t(`adminUsers.statistics.${selectedStatistic.label}`) }}
      </b-badge>
      <span
        v-if="!loadingUsers && !usersLoadError"
        class="text-muted small"
        role="status"
        data-result-count
      >{{ $t('adminUsers.filters.results', { shown: formatCount(filteredUsers.length), total: formatCount(sessionList.length) }) }}</span>
    </div>
    <b-table
      v-if="!usersLoadError"
      ref="sessionTable"
      class="admin-users-table position-relative"
      :items="filteredUsers"
      :busy="loadingUsers"
      responsive
      :fields="tableColumns"
      show-empty
      :empty-text="$t('No Records Found')"
      :sort-compare="compareSubscriptionFields"
    >
      <template #table-busy>
        <div
          class="text-center text-muted py-2"
          role="status"
        >
          {{ $t('adminUsers.statistics.loading') }}
        </div>
      </template>
      <template #cell(index)="data">
        <div class="text-nowrap">
          <span class="align-text-top text-capitalize">{{ data.index + 1 }}</span>
        </div>
      </template>
      <template #cell(createdAt)="data">
        <div class="text-nowrap">
          <span class="align-text-top text-capitalize">{{ new Date(data.item.createdAt).toLocaleString("tr-TR")
          }}</span>
        </div>
      </template>
      <template #cell(approved)="data">
        <FeatherIcon
          v-if="data.item.approved"
          icon="CheckIcon"
          class="text-success"
          size="18"
        />
        <FeatherIcon
          v-else
          icon="XIcon"
          class="text-danger"
          size="18"
        />
      </template>
      <template #cell(publicVisibility)="data">
        <div class="d-flex flex-column align-items-start text-nowrap">
          <b-badge
            :variant="data.item.publicViewEligible ? 'success' : 'secondary'"
            class="mb-25"
          >
            {{ publicViewEligibilityLabel(data.item) }}
          </b-badge>
          <b-badge :variant="publicVisibilityAllowed(data.item) ? 'success' : 'danger'">
            {{ publicVisibilityAllowanceLabel(data.item) }}
          </b-badge>
        </div>
      </template>
      <template #cell(subscriptionStatus)="data">
        <b-badge :variant="subscriptionPresentation(data.item).variant">
          {{ $t(`adminUsers.subscription.${subscriptionPresentation(data.item).label}`) }}
        </b-badge>
      </template>
      <template #cell(accessEndsAt)="data">
        <span class="text-nowrap">{{ formatDate(subscriptionPresentation(data.item).endsAt) }}</span>
      </template>
      <template #cell(actions)="data">
        <div
          v-if="$can('manage', 'all')"
          class="admin-users-actions"
        >
          <b-button
            size="sm"
            variant="primary"
            :disabled="!canGrantFreeAccess(data.item)"
            :title="!canGrantFreeAccess(data.item) ? $t('adminUsers.freeAccess.approvalNote') : ''"
            @click="openGrantModal(data.item)"
          >
            Erişim Ver
          </b-button>
          <b-button
            v-if="freePeriodActive(data.item) || (data.item.subscriptionStatus === 'TRIAL' && !data.item.subscriptionId)"
            size="sm"
            variant="outline-danger"
            @click="revokeAccess(data.item)"
          >
            Kaldır
          </b-button>
          <b-button
            v-if="['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(data.item.subscriptionStatus)"
            size="sm"
            variant="danger"
            @click="cancelSubscription(data.item)"
          >
            Aboneliği İptal Et
          </b-button>
          <b-button
            size="sm"
            :variant="publicVisibilityAllowed(data.item) ? 'outline-danger' : 'outline-success'"
            :disabled="isVisibilityUpdating(data.item)"
            :aria-label="visibilityActionLabel(data.item)"
            :title="visibilityActionLabel(data.item)"
            @click="togglePublicVisibility(data.item)"
          >
            {{ visibilityActionShortLabel(data.item) }}
          </b-button>
        </div>
      </template>
      <div
        slot="empty"
        class="text-center"
      >
        <span class="align-middle">{{ $t("No Records Found") }}</span>
      </div>
    </b-table>

    <!-- Grants a separate free period without changing provider billing. -->
    <b-modal
      id="grant-access-modal"
      v-model="grantModalVisible"
      title="Ücretsiz Kullanım Ver"
      ok-title="Ver"
      cancel-title="Vazgeç"
      :ok-disabled="!isValidDays || granting || !canGrantFreeAccess(grantTarget)"
      @ok.prevent="grantAccess"
    >
      <p v-if="grantTarget">
        <strong>{{ grantTarget.displayName || grantTarget.name }}</strong> ({{ grantTarget.email }})
      </p>
      <p>{{ $t('adminUsers.freeAccess.approvalNote') }}</p>
      <p>{{ $t('adminUsers.freeAccess.notifyNote') }}</p>
      <b-alert
        v-if="grantTarget && grantTarget.freeAccessStartedAt"
        show
        variant="warning"
      >
        {{ $t('adminUsers.freeAccess.alreadyUsedNote', { date: formatDate(grantTarget.freeAccessStartedAt) }) }}
      </b-alert>
      <b-alert
        v-if="grantTarget && grantTarget.subscriptionId"
        show
        variant="info"
      >
        {{ $t('adminUsers.freeAccess.providerNote') }}
      </b-alert>
      <p v-if="grantTarget && freePeriodActive(grantTarget)">
        Mevcut bitiş: {{ formatDate(grantTarget.freeAccessEndsAt) }}
      </p>
      <b-button
        :variant="grantDays === 90 ? 'primary' : 'outline-primary'"
        class="mb-1"
        @click="grantDays = 90"
      >
        3 Ay (90 gün)
      </b-button>
      <b-form-group
        label="Gün sayısı"
        label-for="grant-days"
      >
        <b-form-input
          id="grant-days"
          v-model.number="grantDays"
          type="number"
          min="1"
          max="730"
        />
      </b-form-group>
      <p
        v-if="isValidDays"
        class="mb-0"
      >
        Erişim bitiş tarihi: <strong>{{ grantEndDate }}</strong>
      </p>
      <p
        v-else
        class="text-danger mb-0"
      >
        1 ile 730 arasında bir gün sayısı girin.
      </p>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BCard,
  BTable,
  BBadge,
  BModal,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from '@axios'
import { formatApprovalLifecycleDate } from '@/libs/approvalLifecycle'
import { freeAccessClaimErrorKey } from '@/libs/otpI18nKeys'
import { memberSubscription, matchesMemberSearch } from '@/libs/adminMemberSubscription'

const {
  axiosRoutes: { user },
} = require('../../../G_CONFIG')

const STATISTIC_CARDS = [
  {
    key: 'ALL', label: 'total', icon: 'UsersIcon', variant: 'primary',
  },
  {
    key: 'ACTIVE', label: 'active', icon: 'CheckCircleIcon', variant: 'success',
  },
  {
    key: 'TRIAL', label: 'trial', icon: 'GiftIcon', variant: 'info',
  },
  {
    key: 'CARD_TRIAL', label: 'providerTrial', icon: 'CreditCardIcon', variant: 'info',
  },
  {
    key: 'INACTIVE', label: 'inactive', icon: 'PauseCircleIcon', variant: 'secondary',
  },
  {
    key: 'PAST_DUE', label: 'pastDue', icon: 'AlertCircleIcon', variant: 'warning',
  },
  {
    key: 'CANCELED', label: 'canceled', icon: 'XCircleIcon', variant: 'danger',
  },
]

export default {
  components: {
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BCard,
    BTable,
    BBadge,
    BModal,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      tableColumns: [
        {
          key: 'index', label: 'Sıra', sortable: false, thClass: 'admin-users-index', tdClass: 'admin-users-index',
        },
        {
          key: 'displayName', label: 'İsim', sortable: true, tdClass: 'text-nowrap',
        },
        { key: 'email', label: 'E-posta', sortable: true },
        {
          key: 'phone', label: 'Telefon', sortable: true, tdClass: 'text-nowrap',
        },
        { key: 'createdAt', label: 'Üyelik Tarihi', sortable: true },
        { key: 'approved', label: 'Onay', sortable: true },
        { key: 'publicVisibility', label: this.$t('adminUsers.publicVisibility.column'), sortable: false },
        { key: 'subscriptionStatus', label: 'Abonelik', sortable: true },
        { key: 'accessEndsAt', label: this.$t('adminUsers.subscription.endDate'), sortable: true },
        { key: 'actions', label: 'İşlemler', sortable: false },
        // { key: "permission.name", label: "Yetki Grubu", sortable: true },
      ],
      sessionList: [],
      loadingUsers: true,
      usersLoadError: false,
      selectedStatus: 'ALL',
      nameFilter: '',
      approvalFilter: 'ALL',
      grantModalVisible: false,
      grantTarget: null,
      grantDays: 90,
      granting: false,
      visibilityUpdating: {},
    }
  },
  computed: {
    userStatistics() {
      return STATISTIC_CARDS.map(stat => ({
        ...stat,
        count: stat.key === 'ALL'
          ? this.sessionList.length
          : this.sessionList.filter(item => this.matchesStatistic(item, stat.key)).length,
      }))
    },
    selectedStatistic() {
      return STATISTIC_CARDS.find(stat => stat.key === this.selectedStatus)
    },
    subscriptionPresentations() {
      const now = Date.now()
      return new Map(this.sessionList.map(item => [item, memberSubscription(item, now)]))
    },
    approvalOptions() {
      return ['ALL', 'APPROVED', 'UNAPPROVED'].map(value => ({
        value,
        text: this.$t(`adminUsers.filters.${{ ALL: 'all', APPROVED: 'approved', UNAPPROVED: 'unapproved' }[value]}`),
      }))
    },
    hasFilters() {
      return this.selectedStatus !== 'ALL' || this.approvalFilter !== 'ALL' || Boolean(this.nameFilter)
    },
    filteredUsers() {
      return this.sessionList.filter(item => (
        (this.selectedStatus === 'ALL' || this.matchesStatistic(item, this.selectedStatus))
        && (this.approvalFilter === 'ALL' || (Boolean(item.approved) === (this.approvalFilter === 'APPROVED')))
        && matchesMemberSearch(item, this.nameFilter)
      ))
    },
    isValidDays() {
      return Number.isInteger(this.grantDays) && this.grantDays >= 1 && this.grantDays <= 730
    },
    grantEndDate() {
      if (!this.isValidDays) return ''
      return this.formatDate(new Date(Date.now() + this.grantDays * 24 * 60 * 60 * 1000))
    },
  },
  created() {
    this.getPayments()
    // axios.get(sessionPath.get).then((res) => {
    //   this.sessionList = res.data;
    //   // console.log("object :>> ", this.sessionList);
    // });
  },
  methods: {
    isSysAdminRow(item) { return Boolean(item && item.permission && (item.permission.sysAdmin || item.permission.name === 'sysAdmin')) },
    canGrantFreeAccess(item) { return Boolean(item && (item.approved === true || item.firstApprovedAt) && item.permission && !this.isSysAdminRow(item)) },
    freePeriodActive(item) { return Boolean(item && item.freeAccessStartedAt && item.freeAccessEndsAt && !item.freeAccessEndedReason && new Date(item.freeAccessEndsAt).getTime() > Date.now()) },
    freePeriodDays(item) { return Math.max(0, Math.ceil((new Date(item.freeAccessEndsAt).getTime() - Date.now()) / 86400000)) },
    freePeriodSourceKey(item) {
      return `adminUsers.freeAccess.source.${{ approval: 'approval', 'self-claim': 'selfClaim', 'admin-grant': 'adminGrant' }[item.freeAccessSource]}`
    },
    subscriptionPresentation(item) {
      return this.subscriptionPresentations.get(item) || memberSubscription(item)
    },
    matchesStatistic(item, key) {
      return this.subscriptionPresentation(item).key === key
    },
    compareSubscriptionFields(a, b, key) {
      if (key === 'subscriptionStatus') {
        return this.$t(`adminUsers.subscription.${this.subscriptionPresentation(a).label}`)
          .localeCompare(this.$t(`adminUsers.subscription.${this.subscriptionPresentation(b).label}`), this.$i18n ? this.$i18n.locale : 'tr-TR')
      }
      if (key === 'accessEndsAt') {
        const dateA = Date.parse(this.subscriptionPresentation(a).endsAt) || 0
        const dateB = Date.parse(this.subscriptionPresentation(b).endsAt) || 0
        return dateA - dateB
      }
      return null
    },
    clearFilters() {
      this.nameFilter = ''
      this.approvalFilter = 'ALL'
      this.selectedStatus = 'ALL'
    },
    // disconnectSession(id) {
    //   axios.delete(sessionPath.delete + "/" + id).then((res) => {
    //     this.getSessions();
    //     // console.log("object :>> ", this.sessionList);
    //   });
    // },
    async getPayments() {
      this.loadingUsers = true
      this.usersLoadError = false
      try {
        const { data } = await axios.get(user.getAll)
        this.sessionList = data
      } catch (error) {
        this.sessionList = []
        this.usersLoadError = true
      } finally {
        this.loadingUsers = false
      }
    },
    formatCount(count) {
      return count.toLocaleString(this.$i18n ? this.$i18n.locale : 'tr-TR')
    },
    formatDate(date) {
      return formatApprovalLifecycleDate(date) || '—'
    },
    publicVisibilityAllowed(item) {
      return item.publicVisibilityAllowed !== false
    },
    publicViewEligibilityLabel(item) {
      return this.$t(item.publicViewEligible
        ? 'adminUsers.publicVisibility.eligible'
        : 'adminUsers.publicVisibility.notEligible')
    },
    publicVisibilityAllowanceLabel(item) {
      return this.$t(this.publicVisibilityAllowed(item)
        ? 'adminUsers.publicVisibility.allowed'
        : 'adminUsers.publicVisibility.disallowed')
    },
    visibilityActionLabel(item) {
      return this.$t(this.publicVisibilityAllowed(item)
        ? 'adminUsers.publicVisibility.disallow'
        : 'adminUsers.publicVisibility.allow')
    },
    visibilityActionShortLabel(item) {
      return this.$t(this.publicVisibilityAllowed(item)
        ? 'adminUsers.publicVisibility.hide'
        : 'adminUsers.publicVisibility.show')
    },
    isVisibilityUpdating(item) {
      if (!item) return false
      const { _id: userId } = item
      return Boolean(this.visibilityUpdating[userId])
    },
    async togglePublicVisibility(item) {
      if (!item) return
      const { _id: userId } = item
      if (this.visibilityUpdating[userId]) return
      const allowed = !this.publicVisibilityAllowed(item)
      const label = this.visibilityActionLabel(item)
      this.$set(this.visibilityUpdating, userId, true)
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          this.$t('adminUsers.publicVisibility.confirm', {
            action: label,
            user: item.displayName || item.name,
          }),
          {
            title: label,
            okTitle: label,
            cancelTitle: this.$t('adminUsers.publicVisibility.cancel'),
            okVariant: allowed ? 'success' : 'danger',
          },
        )
        if (!confirmed) return

        const { data } = await axios.patch(
          `${user.publicVisibility}/${userId}/public-visibility`,
          { allowed },
        )
        this.$set(item, 'publicVisibilityAllowed', data.publicVisibilityAllowed)
        this.$bvToast.toast(item.displayName || item.name || '', {
          title: label,
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        this.toastErr(
          error,
          this.$t('adminUsers.publicVisibility.updateError'),
          true,
        )
      } finally {
        this.$delete(this.visibilityUpdating, userId)
      }
    },
    openGrantModal(item) {
      if (!this.canGrantFreeAccess(item)) return
      this.grantTarget = item
      this.grantDays = 90
      this.grantModalVisible = true
    },
    // The server answers with the user's new subscription state, so patch the row
    // in place instead of refetching the whole list.
    applyResponse(item, data) {
      this.$set(item, 'subscriptionStatus', data.subscriptionStatus)
      this.$set(item, 'trialEndsAt', data.trialEndsAt)
      const freeAccessFields = ['freeAccessStartedAt', 'freeAccessEndsAt', 'freeAccessSource', 'freeAccessEndedReason']
      freeAccessFields.forEach(key => {
        if (Object.prototype.hasOwnProperty.call(data, key)) this.$set(item, key, data[key])
      })
    },
    async grantAccess() {
      if (!this.isValidDays || !this.canGrantFreeAccess(this.grantTarget) || this.granting) return
      this.granting = true
      try {
        if (this.grantTarget.subscriptionId) {
          const confirmed = await this.$bvModal.msgBoxConfirm(this.$t('adminUsers.freeAccess.providerNote'), { okTitle: 'Ver', cancelTitle: 'Vazgeç' })
          if (!confirmed) return
        }
        const { _id: grantTargetId } = this.grantTarget
        const { data } = await axios.patch(
          `${user.grantTrial}/${grantTargetId}/trial`,
          { days: this.grantDays },
        )
        this.applyResponse(this.grantTarget, data)
        this.grantModalVisible = false
        this.$bvToast.toast(data.previouslyUsed
          ? this.$t('adminUsers.freeAccess.extendedToast', { date: this.formatDate(data.freeAccessEndsAt) })
          : this.$t('freeAccess.activeBody', { date: this.formatDate(data.freeAccessEndsAt) }), { title: 'Ücretsiz Kullanım', variant: 'success', solid: true })
      } catch (error) {
        this.toastErr(error, this.$t(freeAccessClaimErrorKey(error)), true)
        if (error.response && Number(error.response.data.errorCode) === 400516) {
          const target = this.grantTarget
          const { _id: grantTargetId } = target || {}
          await this.getPayments()
          const refreshedTarget = this.sessionList.find(item => Reflect.get(item, '_id') === grantTargetId)
          if (this.canGrantFreeAccess(refreshedTarget)) this.grantTarget = refreshedTarget
          else {
            this.grantModalVisible = false
            this.grantTarget = null
          }
        }
      } finally {
        this.granting = false
      }
    },
    async revokeAccess(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${item.displayName || item.name} kullanıcısının ücretsiz erişimi kaldırılsın mı?`,
        { okTitle: 'Kaldır', cancelTitle: 'Vazgeç', okVariant: 'danger' },
      )
      if (!confirmed) return
      try {
        const { _id: userId } = item
        const { data } = await axios.patch(`${user.grantTrial}/${userId}/trial`, {
          revoke: true,
        })
        this.applyResponse(item, data)
      } catch (error) {
        this.toastErr(error, 'Erişim kaldırılamadı')
      }
    },
    // Post-pending handoff §7: provider-truth cancel. The server answers with
    // the new status (or alreadyCanceled when iyzico had it canceled already).
    async cancelSubscription(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${item.displayName || item.name} kullanıcısının aboneliği iptal edilsin mi? Erişimi ödenmiş dönemin sonuna kadar devam eder.`,
        {
          title: 'Aboneliği İptal Et',
          okTitle: 'İptal Et',
          cancelTitle: 'Vazgeç',
          okVariant: 'danger',
        },
      )
      if (!confirmed) return
      try {
        const { _id: userId } = item
        const { data } = await axios.delete(`${user.subscriptionAdmin}/${userId}/subscription`)
        this.$set(item, 'subscriptionStatus', data.subscriptionStatus)
        if (Object.prototype.hasOwnProperty.call(data, 'subscriptionAccessEndsAt')) {
          this.$set(item, 'subscriptionAccessEndsAt', data.subscriptionAccessEndsAt)
        }
        if (data.alreadyCanceled) {
          this.$bvToast.toast('Abonelik zaten iptal edilmişti', {
            title: 'Bilgi', variant: 'info', solid: true,
          })
        } else {
          this.$bvToast.toast('Abonelik iptal edildi', {
            title: 'Başarılı', variant: 'success', solid: true,
          })
        }
      } catch (error) {
        this.toastErr(error, 'Abonelik iptal edilemedi')
      }
    },
    toastErr(err, fallback, preferFallback = false) {
      const responseMessage = (err && err.response && err.response.data
          && (err.response.data.message || err.response.data.error))
        || (err && err.message)
      const msg = preferFallback ? fallback : (responseMessage || fallback)
      this.$bvToast.toast(msg, { title: 'Hata', variant: 'danger', solid: true })
    },
    // IPPARSER(ip) {
    //   // return ip.replace("::ffff:", "");
    //   return ip;
    // },
  },
}
</script>

<style lang="scss" scoped>
.admin-users-statistics-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0.5rem;

  @media (max-width: 1199px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  @media (max-width: 575px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.admin-users-statistic {
  padding: 0.5rem 0.65rem;
  min-width: 0;
  background: transparent;
  color: inherit;
  border: 1px solid rgba(128, 128, 128, 0.3);

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 3px;
  }

  &:hover:not(:disabled) {
    border-color: currentColor;
  }

  &:disabled {
    cursor: default;
  }
}

.admin-users-filters {
  display: grid;
  grid-template-columns: minmax(12rem, 1fr) auto auto;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr auto;

    > input {
      grid-column: 1 / -1;
    }
  }
}

::v-deep .admin-users-table .table {
  th,
  td {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  th[aria-sort] {
    padding-right: 1.5rem;
  }
}

::v-deep .admin-users-table .table .admin-users-index {
  width: 3rem;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  text-align: center;
}

.admin-users-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(5.5rem, 1fr));
  gap: 0.5rem;
  width: 12rem;

  .btn {
    min-width: 0;
    white-space: normal;
  }
}
</style>
